<template>
  <SubCard
    :subCardCurrentDataLabel="$t('circleOfDrivers.card.dataLabel')"
    :subCardCurrentInfoText="[
      $t(
        'circleOfDrivers.dropDown.options.' +
          motorContract.circleOfDrivers.circleOfDriversType +
          '_' +
          motorContract.circleOfDrivers.vehicleUsage
      )
    ]"
    :subCardTitle="$t('circleOfDrivers.card.title')"
  >
    <template v-slot:main>
      <div class="card-row">
        <div class="card-row__col">
          <SelectMenue
            @input="fahrerkreisChanged"
            id="fahrerkreis-dropdown"
            style="max-width: 500px"
            :label="
              !selectedFahrerkreis
                ? $t('circleOfDrivers.dropDown.placeholder')
                : $t('circleOfDrivers.card.dataLabel')
            "
            :options="fahrerkreisOptionsSelectable"
            :value="selectedValue"
            idProp="termination-reason"
          >
          </SelectMenue>
        </div>
      </div>
      <p v-if="showBirthYearPartnerDriver">
        {{ titleBirthdayPartner }}
      </p>
      <div v-if="showBirthYearPartnerDriver" class="card-row">
        <div class="card-row__col geburtsjahr-aeltester-juengster-col">
          <BasicTextfield
            @input="updateBirthYearPartnerDriver"
            id="fahrerkreis-birthYearPartnerDriver-input"
            type="number"
            :label="$t('circleOfDrivers.geburtsjahrPartner')"
            :mask="dateFormatPattern"
            :placeholder="dateInputMask"
            :status="birthYearPartnerDriverStatus"
            :value="birthYearPartnerDriver"
            @update:blur="validateBirthYearPartnerDriver"
          >
            <template v-slot:error>{{ birthYearPartnerDriverError }}</template>
          </BasicTextfield>
        </div>
      </div>
      <p v-if="showGeburtsjahrAeltesterAndJuengsterFahrer">
        {{ titleBirthdayYoungestOldest }}
      </p>
      <div
        v-if="showGeburtsjahrAeltesterAndJuengsterFahrer"
        id="geburtsjahr-aeltester-juengster-row"
        class="card-row"
      >
        <div
          class="card-row__col geburtsjahr-aeltester-juengster-col geburtsjahr-juengster-col"
        >
          <BasicTextfield
            @input="updateGeburtsjahrVomJuengstenFahrer"
            id="fahrerkreis-geburtsjahrVomJuengstenFahrer-input"
            type="number"
            :label="$t('circleOfDrivers.geburtsjahrVomJuengstenFahrer')"
            :mask="dateFormatPattern"
            :placeholder="dateInputMask"
            :status="geburtsjahrVomJuengstenFahrerStatus"
            :value="geburtsjahrVomJuengstenFahrer"
            @update:blur="validateGeburtsjahrVomJuengstenFahrer"
          >
            <template v-slot:error>{{ geburtsjahrVomJuengstenFahrerError }}</template>
          </BasicTextfield>
        </div>
        <div class="card-row__col geburtsjahr-aeltester-juengster-col">
          <BasicTextfield
            v-if="showGeburtsjahrAeltesterAndJuengsterFahrer"
            @input="updateGeburtsjahrVomAeltestenFahrer"
            id="fahrerkreis-geburtsjahrVomAeltestenFahrer-input"
            type="number"
            :label="$t('circleOfDrivers.geburtsjahrVomAeltestenFahrer')"
            :mask="dateFormatPattern"
            :placeholder="dateInputMask"
            :status="geburtsjahrVomAeltestenFahrerStatus"
            :value="geburtsjahrVomAeltestenFahrer"
            @update:blur="validateGeburtsjahrVomAeltestenFahrer"
          >
            <template v-slot:error>{{ geburtsjahrVomAeltestenFahrerError }}</template>
          </BasicTextfield>
        </div>
      </div>
    </template>
  </SubCard>
</template>

<script lang="ts" setup>
import { computed, onMounted, type PropType, ref, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Fahrerkreis, MotorContract } from '@/types/motor/MotorContract'
import { FahrerkreisOptions } from '@/types/motor/MotorEnum'
import { BasicTextfield, InputStatus, SelectMenue, SubCard } from 'de-zurich-ui-library'
import dayjs, { Dayjs } from 'dayjs'
import { type OptionDynamicValue } from '@/utilsTenant/MotorUtils'

const fahrerkreisOptions: Ref<Array<OptionDynamicValue<Fahrerkreis>>> = ref([
  {
    label: FahrerkreisOptions.VN_UEBERWIEGEND_PRIVAT,
    value: {
      circleOfDriversType: 'VN',
      vehicleUsage: 'UEBERWIEGEND_PRIVAT',
      option: FahrerkreisOptions.VN_UEBERWIEGEND_PRIVAT
    }
  },
  {
    label: FahrerkreisOptions.VN_UEBERWIEGEND_GESCHAEFTLICH,
    value: {
      circleOfDriversType: 'VN',
      vehicleUsage: 'UEBERWIEGEND_GESCHAEFTLICH',
      option: FahrerkreisOptions.VN_UEBERWIEGEND_GESCHAEFTLICH
    }
  },
  {
    label: FahrerkreisOptions.VN_UND_PARTNER_UEBERWIEGEND_PRIVAT,
    value: {
      circleOfDriversType: 'VN_UND_PARTNER',
      vehicleUsage: 'UEBERWIEGEND_PRIVAT',
      option: FahrerkreisOptions.VN_UND_PARTNER_UEBERWIEGEND_PRIVAT
    }
  },
  {
    label: FahrerkreisOptions.VN_UND_ANDERE_UEBERWIEGEND_PRIVAT,
    value: {
      circleOfDriversType: 'VN_UND_ANDERE',
      vehicleUsage: 'UEBERWIEGEND_PRIVAT',
      option: FahrerkreisOptions.VN_UND_ANDERE_UEBERWIEGEND_PRIVAT
    }
  },
  {
    label: FahrerkreisOptions.VN_UND_ANDERE_UEBERWIEGEND_GESCHAEFTLICH,
    value: {
      circleOfDriversType: 'VN_UND_ANDERE',
      vehicleUsage: 'UEBERWIEGEND_GESCHAEFTLICH',
      option: FahrerkreisOptions.VN_UND_ANDERE_UEBERWIEGEND_GESCHAEFTLICH
    }
  }
])

const fahrerkreisOptionsSelectable: Ref<Array<OptionDynamicValue<Fahrerkreis>>> = ref([])
const dateFormatPattern: string = 'YYYY'
const dateInputMask: string = 'JJJJ'
const birthYearPartnerDriver = ref('')
const birthYearPartnerDriverError = ref('')
const birthYearPartnerDriverStatus = ref<InputStatus>(InputStatus.DEFAULT)
const geburtsjahrVomJuengstenFahrer = ref('')
const geburtsjahrVomJuengstenFahrerError = ref('')
const geburtsjahrVomJuengstenFahrerStatus = ref<InputStatus>(InputStatus.DEFAULT)
const geburtsjahrVomAeltestenFahrer = ref('')
const geburtsjahrVomAeltestenFahrerError = ref('')
const geburtsjahrVomAeltestenFahrerStatus = ref<InputStatus>(InputStatus.DEFAULT)
const showBirthYearPartnerDriverFahrerkreisList: FahrerkreisOptions[] = [
  FahrerkreisOptions.VN_UND_PARTNER_UEBERWIEGEND_PRIVAT
]
const showBirthYearPartnerDriver = ref(false)
const showGeburtsjahrAeltesterAndJuengsterFahrerkreisList: FahrerkreisOptions[] = [
  FahrerkreisOptions.VN_UND_ANDERE_UEBERWIEGEND_PRIVAT,
  FahrerkreisOptions.VN_UND_ANDERE_UEBERWIEGEND_GESCHAEFTLICH
]
const showGeburtsjahrAeltesterAndJuengsterFahrer = ref(false)
const selectedFahrerkreis: Ref<Fahrerkreis | null> = ref(null)
const minAge = 17
const maxAge = 99
const status = ref<InputStatus>(InputStatus.DEFAULT)
const fahrerkreisValue = ref()
const selectedValue = ref()

const { t } = useI18n()

const titleBirthdayPartner = computed(
  () => t('circleOfDrivers.card.titleBirthdayPartner') as string
)
const titleBirthdayYoungestOldest = computed(
  () => t('circleOfDrivers.card.titleBirthdayYoungestOldest') as string
)

const props = defineProps({
  motorContract: {
    type: Object as PropType<MotorContract>,
    required: true
  },
  value: {
    type: Object as PropType<Fahrerkreis>,
    required: true
  }
})

const emit = defineEmits<{
  status: [status: InputStatus]
  'update:value': [value: Fahrerkreis]
}>()

onMounted(() => {
  fahrerkreisValue.value = props.value
  fahrerkreisOptions.value.forEach((option: OptionDynamicValue<Fahrerkreis>) => {
    option.value.option = option.label as unknown as FahrerkreisOptions
    option.label = t('circleOfDrivers.dropDown.options.' + option.label)
    return option
  })

  const fahrerkreisSelected = fahrerkreisOptions.value.find(
    (option) =>
      option.value.circleOfDriversType === fahrerkreisValue.value.circleOfDriversType &&
      option.value.vehicleUsage === fahrerkreisValue.value.vehicleUsage
  )
  if (fahrerkreisSelected) {
    fahrerkreisOptionsSelectable.value = fahrerkreisOptions.value.filter(
      (o) => o !== fahrerkreisSelected
    )
  }
})

function updateBirthYearPartnerDriver(val: string) {
  birthYearPartnerDriver.value = val
  validateBirthYearPartnerDriver()
}

function validateBirthYearPartnerDriver(): boolean {
  const birthYearPartnerDriverDate = convertStrToMoment(birthYearPartnerDriver.value)
  if (!birthYearPartnerDriverDate.isValid()) {
    birthYearPartnerDriverStatus.value = InputStatus.ERROR
    birthYearPartnerDriverError.value = t('circleOfDrivers.validationMessages.dateFormat')

    checkStatusAndEmit()
    return false
  }

  const age = birthYearPartnerDriverDate.diff(new Date(), 'year') * -1
  if (age < minAge || age > maxAge) {
    birthYearPartnerDriverStatus.value = InputStatus.ERROR
    birthYearPartnerDriverError.value = t(
      'circleOfDrivers.validationMessages.notInRange',
      {
        minAge: minAge,
        maxAge: maxAge
      }
    )

    checkStatusAndEmit()
    return false
  }

  birthYearPartnerDriverError.value = ''
  birthYearPartnerDriverStatus.value = InputStatus.VALID
  checkStatusAndEmit()
  return true
}

function convertStrToMoment(val: string | null): Dayjs {
  const valStr: string = val === null ? '' : val
  return dayjs(valStr, dateFormatPattern, true)
}

function updateGeburtsjahrVomJuengstenFahrer(val: string) {
  geburtsjahrVomJuengstenFahrer.value = val
}

function validateGeburtsjahrVomJuengstenFahrer(): boolean {
  const geburtsjahrVomJuengstenFahrerDate = convertStrToMoment(
    geburtsjahrVomJuengstenFahrer.value
  )
  if (!geburtsjahrVomJuengstenFahrerDate.isValid()) {
    geburtsjahrVomJuengstenFahrerStatus.value = InputStatus.ERROR
    geburtsjahrVomJuengstenFahrerError.value = t(
      'circleOfDrivers.validationMessages.dateFormat'
    )

    checkStatusAndEmit()
    return false
  }

  const age = geburtsjahrVomJuengstenFahrerDate.diff(new Date(), 'year') * -1
  if (age < minAge || age > maxAge) {
    geburtsjahrVomJuengstenFahrerStatus.value = InputStatus.ERROR
    geburtsjahrVomJuengstenFahrerError.value = t(
      'circleOfDrivers.validationMessages.notInRange',
      {
        minAge: minAge,
        maxAge: maxAge
      }
    )

    checkStatusAndEmit()
    return false
  }

  const geburtsjahrVomAeltestenFahrerDate = convertStrToMoment(
    geburtsjahrVomAeltestenFahrer.value
  )
  if (geburtsjahrVomAeltestenFahrerDate.isAfter(geburtsjahrVomJuengstenFahrerDate)) {
    geburtsjahrVomJuengstenFahrerStatus.value = InputStatus.ERROR
    geburtsjahrVomJuengstenFahrerError.value = t(
      'circleOfDrivers.validationMessages.beforeAeltesterFahrer'
    )

    checkStatusAndEmit()
    return false
  }

  geburtsjahrVomJuengstenFahrerError.value = ''
  geburtsjahrVomJuengstenFahrerStatus.value = InputStatus.VALID
  checkStatusAndEmit()
  return true
}

function updateGeburtsjahrVomAeltestenFahrer(val: string) {
  geburtsjahrVomAeltestenFahrer.value = val
}

function validateGeburtsjahrVomAeltestenFahrer(): boolean {
  const geburtsjahrVomAeltestenFahrerDate = convertStrToMoment(
    geburtsjahrVomAeltestenFahrer.value
  )

  if (!geburtsjahrVomAeltestenFahrerDate.isValid()) {
    geburtsjahrVomAeltestenFahrerStatus.value = InputStatus.ERROR
    geburtsjahrVomAeltestenFahrerError.value = t(
      'circleOfDrivers.validationMessages.dateFormat'
    )
    checkStatusAndEmit()
    return false
  }

  const age = geburtsjahrVomAeltestenFahrerDate.diff(new Date(), 'year') * -1
  if (age < minAge || age > maxAge) {
    geburtsjahrVomAeltestenFahrerStatus.value = InputStatus.ERROR
    geburtsjahrVomAeltestenFahrerError.value = t(
      'circleOfDrivers.validationMessages.notInRange',
      {
        minAge: minAge,
        maxAge: maxAge
      }
    )

    checkStatusAndEmit()
    return false
  }

  const jahrVomJuengstenFahrer = convertStrToMoment(geburtsjahrVomJuengstenFahrer.value)
  if (geburtsjahrVomAeltestenFahrerDate.isAfter(jahrVomJuengstenFahrer)) {
    geburtsjahrVomAeltestenFahrerStatus.value = InputStatus.ERROR
    geburtsjahrVomAeltestenFahrerError.value = t(
      'circleOfDrivers.validationMessages.afterJuengsterFahrer'
    )

    checkStatusAndEmit()
    return false
  }

  geburtsjahrVomAeltestenFahrerError.value = ''
  geburtsjahrVomAeltestenFahrerStatus.value = InputStatus.VALID
  checkStatusAndEmit()
  return true
}

function fahrerkreisChanged(value: string): void {
  // Suche das passende OptionDynamicValue<Fahrerkreis> anhand des Labels
  const optionSelected = fahrerkreisOptionsSelectable.value.find(
    (option) => option.label === value
  )
  if (optionSelected) {
    selectedFahrerkreis.value = optionSelected.value
    if (selectedFahrerkreis.value.option) {
      showBirthYearPartnerDriver.value =
        showBirthYearPartnerDriverFahrerkreisList.includes(
          selectedFahrerkreis.value.option
        )
      showGeburtsjahrAeltesterAndJuengsterFahrer.value =
        showGeburtsjahrAeltesterAndJuengsterFahrerkreisList.includes(
          selectedFahrerkreis.value.option
        )
    }

    if (
      showBirthYearPartnerDriver.value &&
      (isNumberUndefinedAndGreaterThanZero(
        fahrerkreisValue.value.birthYearOldestDriver
      ) ||
        isNumberUndefinedAndGreaterThanZero(
          fahrerkreisValue.value.birthYearYoungestDriver
        ))
    ) {
      resetDateInputs()
    }

    if (
      showGeburtsjahrAeltesterAndJuengsterFahrerkreisList &&
      isNumberUndefinedAndGreaterThanZero(fahrerkreisValue.value.birthYearPartnerDriver)
    ) {
      resetDateInputs()
    }

    if (selectedFahrerkreis.value.circleOfDriversType === 'VN') {
      resetDateInputs()
    }
  }
  checkStatusAndEmit()
}

function isNumberUndefinedAndGreaterThanZero(nbr: number | undefined): boolean {
  return nbr ? nbr > 0 : false
}

function resetDateInputs() {
  fahrerkreisValue.value.birthYearPartnerDriver = undefined
  birthYearPartnerDriver.value = ''

  fahrerkreisValue.value.birthYearOldestDriver = undefined
  fahrerkreisValue.value.birthYearYoungestDriver = undefined
  geburtsjahrVomJuengstenFahrer.value = ''
  geburtsjahrVomAeltestenFahrer.value = ''
}

function checkStatusAndEmit(): void {
  if (showBirthYearPartnerDriver.value) {
    status.value = birthYearPartnerDriverStatus.value

    fahrerkreisValue.value.birthYearPartnerDriver = parseInt(
      birthYearPartnerDriver.value,
      10
    )
  } else if (showGeburtsjahrAeltesterAndJuengsterFahrer.value) {
    status.value =
      geburtsjahrVomAeltestenFahrerStatus.value === InputStatus.VALID &&
      geburtsjahrVomJuengstenFahrerStatus.value === InputStatus.VALID
        ? InputStatus.VALID
        : InputStatus.ERROR

    fahrerkreisValue.value.birthYearYoungestDriver = parseInt(
      geburtsjahrVomJuengstenFahrer.value,
      10
    )
    fahrerkreisValue.value.birthYearOldestDriver = parseInt(
      geburtsjahrVomAeltestenFahrer.value,
      10
    )
  } else if (selectedFahrerkreis.value?.circleOfDriversType === 'VN') {
    status.value = InputStatus.VALID
  } else {
    status.value = InputStatus.ERROR
  }

  if (selectedFahrerkreis.value) {
    fahrerkreisValue.value.circleOfDriversType =
      selectedFahrerkreis.value.circleOfDriversType
    fahrerkreisValue.value.vehicleUsage = selectedFahrerkreis.value.vehicleUsage
  }
  emit('update:value', fahrerkreisValue.value)
  emit('status', status.value)
}
</script>

<style lang="scss" scoped>
.card-row {
  column-gap: 32px;
}

.geburtsjahr-aeltester-juengster-col {
  width: 100%;
  @media (min-width: 600px) {
    max-width: 234px;
  }
}

.geburtsjahr-juengster-col {
  @media (min-width: 600px) {
    margin-right: calc(var(--baseline) * 2);
  }
}
</style>
